// src/pages/BlockEvaluationPage.tsx
import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Button, Card, CardContent, Grid, Dialog, DialogTitle,
    DialogContent, DialogActions, TextField, MenuItem, IconButton, List,
    ListItem, ListItemText, ListItemSecondaryAction, Divider, Paper, Chip,
    FormControl, InputLabel, Select, InputAdornment, Stack, Tabs, Tab,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Stepper, Step, StepLabel, Tooltip,
    Avatar, Fade, CircularProgress
} from '@mui/material';
import api from '../services/api';
import { designTokens } from '../theme/designTokens';
import AddIcon from '@mui/icons-material/Add';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import DeleteIcon from '@mui/icons-material/Delete';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import PercentIcon from '@mui/icons-material/Percent';
import DescriptionIcon from '@mui/icons-material/Description';
import FolderIcon from '@mui/icons-material/Folder';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import InfoIcon from '@mui/icons-material/Info';
import AssignmentIcon from '@mui/icons-material/Assignment';
import CloseIcon from '@mui/icons-material/Close';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import HistoryIcon from '@mui/icons-material/History';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { useToast } from '../context/ToastContext';
import { useAuth } from '../context/AuthContext';
import LottieLoading from '../components/common/LottieLoading';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';
import { signData } from '../utils/cryptoUtil';

interface Evaluation {
    _id: string;
    title: string;
    timeLimitMinutes: number;
    openPercentage: number;
    statementText?: string;
    statementFiles?: string[];
    blockId?: {
        _id: string;
        title: string;
    };
}

interface Bloc {
    _id: string;
    title: string;
    parcoursId?: {
        title: string;
    };
}

interface TrackingKPIs {
    unopened: number;
    inProgress: number;
    graded: number;
    averageGrade: number;
}

interface TrackingItem {
    learner: { id: string; firstName: string; lastName: string };
    evaluation: { id: string; title: string; timeLimitMinutes: number; openPercentage: number; blocTitle: string };
    submission: {
        id: string; startedAt: string; deadline: string; submittedAt: string; gradedAt: string;
        answers: string; files: { name: string; url: string; type: string }[]; grade: number; feedback: string;
        trainerSignature?: string; serverSignature?: string;
        deadlineExtensions?: { oldDeadline: string; newDeadline: string; reason: string; date: string }[];
    } | null;
    status: string;
}

type TimeUnit = 'minutes' | 'hours' | 'days';

const BlockEvaluationPage: React.FC = () => {
    const [evaluations, setEvaluations] = useState<Evaluation[]>([]);
    const [blocs, setBlocs] = useState<Bloc[]>([]);
    const [openDialog, setOpenDialog] = useState(false);
    const [form, setForm] = useState({
        title: '',
        blockId: '',
        timeLimit: 60,
        timeUnit: 'minutes' as TimeUnit,
        openPercentage: 0,
        statementText: ''
    });
    const [files, setFiles] = useState<File[]>([]);

    const { showToast } = useToast();
    const { user, signingPrivateKey } = useAuth();
    const isSuperAdmin = user?.role === 'super-admin';
    const isTrainerOrAdmin = ['super-admin', 'of-admin', 'trainer'].includes(user?.role || '');

    // Tracking states
    const [currentTab, setCurrentTab] = useState(isSuperAdmin ? 0 : 1);
    const [trackingKpis, setTrackingKpis] = useState<TrackingKPIs | null>(null);
    const [trackingList, setTrackingList] = useState<TrackingItem[]>([]);
    const [selectedTracking, setSelectedTracking] = useState<TrackingItem | null>(null);
    const [openTrackingDialog, setOpenTrackingDialog] = useState(false);
    const [openExtendDialog, setOpenExtendDialog] = useState(false);
    const [extendForm, setExtendForm] = useState({ date: '', time: '', reason: '' });
    const [pageLoading, setPageLoading] = useState(true);

    const [selectedEvaluation, setSelectedEvaluation] = useState<Evaluation | null>(null);
    const [openModelDetailDialog, setOpenModelDetailDialog] = useState(false);

    // Grading state
    const [gradingForm, setGradingForm] = useState({ grade: '', feedback: '' });
    const [isGrading, setIsGrading] = useState(false);

    useEffect(() => {
        if (!isSuperAdmin) setCurrentTab(1);
    }, [isSuperAdmin]);

    const fetchEvals = async () => {
        try {
            const res = await api.get('/api/block-evaluations');
            setEvaluations(res.data);
        } catch (err) {
            console.error('Error fetching evaluations', err);
            showToast('Erreur lors du chargement des évaluations', 'error');
        }
    };

    const fetchBlocs = async () => {
        try {
            const res = await api.get('/api/block-evaluations/blocs');
            setBlocs(res.data);
        } catch (err) {
            console.error('Error fetching blocs', err);
            showToast('Erreur lors du chargement des blocs', 'error');
        }
    };

    const fetchTracking = async () => {
        try {
            const res = await api.get('/api/block-evaluations/tracking');
            setTrackingKpis(res.data.kpis);
            setTrackingList(res.data.tracking);
        } catch (err) {
            console.error('Error fetching tracking', err);
            showToast('Erreur lors du chargement du suivi', 'error');
        }
    }

    useEffect(() => {
        const initData = async () => {
            setPageLoading(true);
            await Promise.all([
                fetchEvals(),
                fetchBlocs(),
                fetchTracking()
            ]);
            setPageLoading(false);
        };
        initData();
    }, []);

    const handleOpen = () => setOpenDialog(true);
    const handleClose = () => {
        setOpenDialog(false);
        setForm({ title: '', blockId: '', timeLimit: 60, timeUnit: 'minutes', openPercentage: 0, statementText: '' });
        setFiles([]);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setForm({ ...form, [e.target.name]: e.target.value });
    };

    const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            setFiles([...files, ...Array.from(e.target.files)]);
        }
    };

    const handleRemoveFile = (index: number) => {
        setFiles(files.filter((_, i) => i !== index));
    };

    const convertToMinutes = (value: number, unit: TimeUnit): number => {
        switch (unit) {
            case 'hours': return value * 60;
            case 'days': return value * 60 * 24;
            default: return value;
        }
    };

    const formatTimeLimit = (minutes: number): string => {
        if (minutes < 60) return `${minutes} min`;
        if (minutes < 1440) return `${Math.floor(minutes / 60)}h ${minutes % 60 > 0 ? minutes % 60 + 'min' : ''}`.trim();
        const days = Math.floor(minutes / 1440);
        const hours = Math.floor((minutes % 1440) / 60);
        return `${days}j ${hours > 0 ? hours + 'h' : ''}`.trim();
    };

    const handleSubmit = async () => {
        try {
            // Convert files to base64
            const filePromises = files.map(file => {
                return new Promise<string>((resolve, reject) => {
                    const reader = new FileReader();
                    reader.onloadend = () => resolve(reader.result as string);
                    reader.onerror = reject;
                    reader.readAsDataURL(file);
                });
            });

            const statementFiles = await Promise.all(filePromises);
            const timeLimitMinutes = convertToMinutes(form.timeLimit, form.timeUnit);

            await api.post('/api/block-evaluations', {
                title: form.title,
                blockId: form.blockId,
                timeLimitMinutes,
                openPercentage: form.openPercentage,
                statementText: form.statementText,
                statementFiles
            });

            showToast('Évaluation créée avec succès', 'success');
            fetchEvals();
            handleClose();
        } catch (err) {
            console.error('Error creating evaluation', err);
            showToast('Erreur lors de la création de l\'évaluation', 'error');
        }
    };

    // Tracking Dialog Helpers
    const handleOpenTracking = (item: TrackingItem) => {
        setSelectedTracking(item);
        setOpenTrackingDialog(true);
    };

    const handleCloseTracking = () => {
        setOpenTrackingDialog(false);
        setSelectedTracking(null);
    };

    const handleOpenExtendDialog = () => setOpenExtendDialog(true);
    const handleCloseExtendDialog = () => {
        setOpenExtendDialog(false);
        setExtendForm({ date: '', time: '', reason: '' });
    };

    const handleOpenModelDetail = (ev: Evaluation) => {
        setSelectedEvaluation(ev);
        setOpenModelDetailDialog(true);
    };

    const handleCloseModelDetail = () => {
        setOpenModelDetailDialog(false);
        setSelectedEvaluation(null);
    };

    const handleExtendDeadline = async () => {
        if (!extendForm.date || !extendForm.reason || !selectedTracking?.submission?.id) return;
        try {
            const newDeadline = new Date(`${extendForm.date}T${extendForm.time || '23:59'}`);
            await api.post(`/api/block-evaluations/submissions/${selectedTracking.submission.id}/extend-deadline`, {
                newDeadline: newDeadline.toISOString(),
                reason: extendForm.reason
            });
            showToast('Date limite repoussée avec succès', 'success');
            handleCloseExtendDialog();
            fetchTracking(); // Refresh the data
            handleCloseTracking(); // Optional, or wait for next open
        } catch (err) {
            console.error('Error extending deadline', err);
            showToast('Erreur lors du report de la date limite', 'error');
        }
    };

    const handleGradeSubmission = async () => {
        if (!selectedTracking?.submission || !user) return;
        if (gradingForm.grade === '') {
            showToast('Veuillez saisir une note', 'error');
            return;
        }

        setIsGrading(true);
        try {
            const grade = parseFloat(gradingForm.grade);
            const feedback = gradingForm.feedback;
            const timestamp = Date.now();
            
            // Payload for signature: grade + feedback + learnerId + submissionId + timestamp
            const payload = JSON.stringify({
                grade,
                feedback,
                learnerId: selectedTracking.learner.id,
                submissionId: selectedTracking.submission.id,
                timestamp
            });

            let trainerSignature = null;
            if (signingPrivateKey) {
                trainerSignature = await signData(payload, signingPrivateKey);
            } else {
                console.warn("No signing key available. Grade will be sent without signature.");
            }

            await api.patch(`/api/block-evaluations/${selectedTracking.evaluation.id}/submissions/${selectedTracking.submission.id}`, {
                grade,
                feedback,
                trainerSignature,
                signingTimestamp: timestamp
            });

            showToast('Évaluation corrigée et signée avec succès', 'success');
            setGradingForm({ grade: '', feedback: '' });
            fetchTracking();
            handleCloseTracking();
        } catch (err) {
            console.error('Error grading submission', err);
            showToast('Erreur lors de la correction', 'error');
        } finally {
            setIsGrading(false);
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'Corrigé et validé': return 'success';
            case 'Corrigé et non validée': return 'error';
            case 'Rendu': return 'info';
            case 'En cours': return 'warning';
            case 'Dépassé': return 'error';
            case 'Non ouverte': return 'default';
            default: return 'default';
        }
    };

    const getStepperActiveStep = (status: string) => {
        switch (status) {
            case 'Fermée': return 0;
            case 'Non ouverte': return 1;
            case 'En cours': return 2;
            case 'Dépassé': return 2;
            case 'Rendu': return 3;
            case 'Corrigé et validé':
            case 'Corrigé et non validée': return 4;
            default: return 0;
        }
    };

    const renderTrackingTab = () => (
        <Box sx={{ mt: 3 }}>
            <TableContainer component={Paper} sx={{ background: 'rgba(255,255,255,0.9)', backdropFilter: 'blur(10px)', borderRadius: 4 }}>
                <Table>
                    <TableHead sx={{ background: 'rgba(0,0,0,0.02)' }}>
                        <TableRow>
                            <TableCell>Apprenant</TableCell>
                            <TableCell>Évaluation</TableCell>
                            <TableCell>Date limite</TableCell>
                            <TableCell>Statut</TableCell>
                            <TableCell align="right">Actions</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {trackingList.length === 0 ? (
                            <TableRow>
                                <TableCell colSpan={5} align="center" sx={{ py: 3, color: '#64748b' }}>
                                    Aucune donnée de suivi disponible.
                                </TableCell>
                            </TableRow>
                        ) : (
                            trackingList.map((item, index) => (
                                <TableRow key={index} hover>
                                    <TableCell>
                                        <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                            {item.learner.firstName} {item.learner.lastName}
                                        </Typography>
                                    </TableCell>
                                    <TableCell>
                                        <Typography variant="body2">{item.evaluation.title}</Typography>
                                        <Typography variant="caption" sx={{ color: '#64748b' }}>{item.evaluation.blocTitle}</Typography>
                                    </TableCell>
                                    <TableCell>
                                        {item.submission?.deadline ? format(new Date(item.submission.deadline), 'dd MMM yyyy HH:mm', { locale: fr }) : '-'}
                                    </TableCell>
                                    <TableCell>
                                        <Chip label={item.status} color={getStatusColor(item.status) as any} size="small" />
                                    </TableCell>
                                    <TableCell align="right">
                                        <Button size="small" variant="outlined" startIcon={<VisibilityIcon />} onClick={() => handleOpenTracking(item)}>
                                            Détails
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Box>
    );

    if (pageLoading) {
        return <LottieLoading message="Chargement des données de suivi..." fullScreen={true} size={200} />;
    }

    return (
        <Box sx={{
            minHeight: '100vh',
            background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e9f2 100%)',
            p: { xs: 2, md: 4 }
        }}>
            {/* Hero Header */}
            <Paper
                elevation={0}
                sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    borderRadius: 4,
                    p: { xs: 3, md: 5 },
                    mb: 4,
                    color: 'white',
                    position: 'relative',
                    overflow: 'hidden',
                }}
            >
                {/* Decorative circles */}
                <Box sx={{ position: 'absolute', top: -50, right: -50, width: 200, height: 200, borderRadius: '50%', background: 'rgba(255,255,255,0.1)' }} />
                <Box sx={{ position: 'absolute', bottom: -30, right: 100, width: 120, height: 120, borderRadius: '50%', background: 'rgba(255,255,255,0.08)' }} />

                <Grid container spacing={4} alignItems="center">
                    <Grid size={{ xs: 12, md: 6 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                            <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 56, height: 56, backdropFilter: 'blur(10px)' }}>
                                <AssignmentIcon sx={{ fontSize: 28 }} />
                            </Avatar>
                            <Box>
                                <Typography variant="h4" sx={{ fontWeight: 700, fontFamily: 'Quicksand, sans-serif' }}>Évaluation de Bloc</Typography>
                                <Typography variant="body1" sx={{ opacity: 0.9 }}>Créez et gérez les évaluations pour vos blocs de compétences</Typography>
                            </Box>
                        </Box>
                        {isSuperAdmin && currentTab === 0 && (
                            <Button
                                variant="contained"
                                size="large"
                                startIcon={<AddIcon />}
                                onClick={handleOpen}
                                sx={{
                                    mt: 2, bgcolor: 'white', color: '#667eea', fontWeight: 700, px: 4, py: 1.5,
                                    borderRadius: 3, textTransform: 'none', fontSize: '1rem',
                                    boxShadow: '0 4px 14px rgba(0,0,0,0.2)',
                                    '&:hover': { bgcolor: 'rgba(255,255,255,0.95)', transform: 'translateY(-2px)', boxShadow: '0 6px 20px rgba(0,0,0,0.25)' },
                                    transition: 'all 0.2s ease'
                                }}
                            >
                                Créer une évaluation
                            </Button>
                        )}
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                        {trackingKpis && currentTab === 1 && (
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 3 }}>
                                    <Box sx={{ bgcolor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)', borderRadius: 3, p: 2, textAlign: 'center' }}>
                                        <Typography variant="h3" sx={{ fontWeight: 700 }}>{trackingKpis.unopened}</Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.9 }}>Non ouvertes</Typography>
                                    </Box>
                                </Grid>
                                <Grid size={{ xs: 3 }}>
                                    <Box sx={{ bgcolor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)', borderRadius: 3, p: 2, textAlign: 'center' }}>
                                        <Typography variant="h3" sx={{ fontWeight: 700 }}>{trackingKpis.inProgress}</Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.9 }}>En cours</Typography>
                                    </Box>
                                </Grid>
                                <Grid size={{ xs: 3 }}>
                                    <Box sx={{ bgcolor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)', borderRadius: 3, p: 2, textAlign: 'center' }}>
                                        <Typography variant="h3" sx={{ fontWeight: 700 }}>{trackingKpis.graded}</Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.9 }}>Corrigées</Typography>
                                    </Box>
                                </Grid>
                                <Grid size={{ xs: 3 }}>
                                    <Box sx={{ bgcolor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)', borderRadius: 3, p: 2, textAlign: 'center' }}>
                                        <Typography variant="h3" sx={{ fontWeight: 700 }}>{trackingKpis.averageGrade}</Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.9 }}>Moyenne</Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        )}
                        {currentTab === 0 && (
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 6 }}>
                                    <Box sx={{ bgcolor: 'rgba(255,255,255,0.15)', backdropFilter: 'blur(10px)', borderRadius: 3, p: 2, textAlign: 'center' }}>
                                        <Typography variant="h3" sx={{ fontWeight: 700 }}>{evaluations.length}</Typography>
                                        <Typography variant="body2" sx={{ opacity: 0.9 }}>Modèles</Typography>
                                    </Box>
                                </Grid>
                            </Grid>
                        )}
                    </Grid>
                </Grid>
            </Paper>

            {isSuperAdmin && (
                <Box sx={{ borderBottom: 1, borderColor: 'divider', mt: 2 }}>
                    <Tabs value={currentTab} onChange={(e, v) => setCurrentTab(v)} textColor="primary" indicatorColor="primary">
                        <Tab label="Modèles d'Évaluation" />
                        <Tab label="Suivi des Apprenants" />
                    </Tabs>
                </Box>
            )}

            {isSuperAdmin && currentTab === 0 && (
                <>
                    {evaluations.length === 0 ? (
                        <Paper sx={{
                            p: 6,
                            textAlign: 'center',
                            mt: 4,
                            background: 'rgba(255,255,255,0.05)',
                            backdropFilter: 'blur(10px)',
                            border: '1px solid rgba(255,255,255,0.1)'
                        }}>
                            <DescriptionIcon sx={{ fontSize: 64, color: 'rgba(255,255,255,0.3)', mb: 2 }} />
                            <Typography variant="h6" sx={{ color: 'rgba(255,255,255,0.7)' }}>
                                Aucune évaluation créée
                            </Typography>
                            <Typography variant="body2" sx={{ color: 'rgba(255,255,255,0.5)', mt: 1 }}>
                                Commencez par créer votre première évaluation de bloc
                            </Typography>
                        </Paper>
                    ) : (
                        <Grid container spacing={3} sx={{ mt: 2 }}>
                            {evaluations.map((ev, index) => (
                                <Grid size={{ xs: 12, md: 6, lg: 4 }} key={ev._id}>
                                    <Fade in timeout={300 + index * 100}>
                                        <Card
                                            onClick={() => handleOpenModelDetail(ev)}
                                            sx={{
                                                ...designTokens.glassCard,
                                                height: '100%',
                                                display: 'flex',
                                                flexDirection: 'column',
                                                borderRadius: 4,
                                                cursor: 'pointer',
                                                transition: 'all 0.3s ease',
                                                background: 'rgba(255, 255, 255, 0.9)',
                                                backdropFilter: 'blur(10px)',
                                                border: '1px solid rgba(0, 0, 0, 0.1)',
                                                '&:hover': {
                                                    transform: 'translateY(-8px)',
                                                    boxShadow: '0 12px 40px rgba(102, 126, 234, 0.25)',
                                                    background: 'rgba(255, 255, 255, 1)',
                                                }
                                            }}>
                                            <CardContent sx={{ flexGrow: 1 }}>
                                                <Typography variant="h6" sx={{ mb: 2, fontWeight: 600, color: '#1e293b' }}>
                                                    {ev.title}
                                                </Typography>
                                                {ev.blockId && (
                                                    <Chip
                                                        icon={<FolderIcon sx={{ color: '#667eea !important' }} />}
                                                        label={ev.blockId.title}
                                                        size="small"
                                                        sx={{
                                                            mb: 2,
                                                            background: 'rgba(103, 126, 234, 0.15)',
                                                            color: '#667eea',
                                                            fontWeight: 500,
                                                            border: '1px solid rgba(103, 126, 234, 0.3)'
                                                        }}
                                                    />
                                                )}
                                                <Stack spacing={1}>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <AccessTimeIcon sx={{ fontSize: 18, color: '#64748b' }} />
                                                        <Typography variant="body2" sx={{ color: '#475569' }}>
                                                            Temps limite : {formatTimeLimit(ev.timeLimitMinutes)}
                                                        </Typography>
                                                    </Box>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <PercentIcon sx={{ fontSize: 18, color: '#64748b' }} />
                                                        <Typography variant="body2" sx={{ color: '#475569' }}>
                                                            Ouverture : {ev.openPercentage}% du bloc
                                                        </Typography>
                                                    </Box>
                                                </Stack>
                                            </CardContent>
                                        </Card>
                                    </Fade>
                                </Grid>
                            ))}
                        </Grid>
                    )}
                </>
            )}

            {currentTab === 1 && renderTrackingTab()}

            <Dialog
                open={openDialog}
                onClose={handleClose}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        background: 'linear-gradient(135deg, #1e293b 0%, #0f172a 100%)',
                        backdropFilter: 'blur(20px)',
                        border: '1px solid rgba(255,255,255,0.1)',
                        boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
                        color: '#e2e8f0'
                    }
                }}
            >
                <DialogTitle sx={{
                    borderBottom: '1px solid rgba(255,255,255,0.1)',
                    pb: 2
                }}>
                    <Typography variant="h5" sx={{ fontWeight: 600, color: '#f1f5f9' }}>
                        Créer une nouvelle évaluation
                    </Typography>
                    <Typography variant="body2" sx={{ color: '#94a3b8', mt: 0.5 }}>
                        Configurez les paramètres de votre évaluation
                    </Typography>
                </DialogTitle>
                <DialogContent sx={{ pt: 3 }}>
                    <Stack spacing={3} sx={{ marginTop: 2 }}>
                        {/* Titre */}
                        <TextField
                            label="Titre de l'évaluation"
                            name="title"
                            fullWidth
                            value={form.title}
                            onChange={handleChange}
                            placeholder="Ex: Évaluation finale du bloc 1"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <DescriptionIcon sx={{ color: '#94a3b8' }} />
                                    </InputAdornment>
                                ),
                            }}
                            sx={{
                                '& .MuiInputBase-input': { color: '#e2e8f0' },
                                '& .MuiInputLabel-root': { color: '#94a3b8' },
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': { borderColor: 'rgba(148, 163, 184, 0.3)' },
                                    '&:hover fieldset': { borderColor: '#94a3b8' },
                                    '&.Mui-focused fieldset': { borderColor: '#667eea' }
                                }
                            }}
                        />

                        {/* Sélection du bloc */}
                        <FormControl fullWidth>
                            <InputLabel sx={{ color: '#94a3b8' }}>Bloc de compétences</InputLabel>
                            <Select
                                name="blockId"
                                value={form.blockId}
                                onChange={(e) => setForm({ ...form, blockId: e.target.value })}
                                label="Bloc de compétences"
                                startAdornment={
                                    <InputAdornment position="start">
                                        <FolderIcon sx={{ color: '#94a3b8', ml: 1 }} />
                                    </InputAdornment>
                                }
                                sx={{
                                    color: '#e2e8f0',
                                    '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(148, 163, 184, 0.3)' },
                                    '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#94a3b8' },
                                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#667eea' }
                                }}
                            >
                                {blocs.map((bloc) => (
                                    <MenuItem key={bloc._id} value={bloc._id}>
                                        <Box>
                                            <Typography variant="body1" sx={{ color: '#1e293b' }}>{bloc.title}</Typography>
                                            {bloc.parcoursId && (
                                                <Typography variant="caption" sx={{ color: '#64748b' }}>
                                                    {bloc.parcoursId.title}
                                                </Typography>
                                            )}
                                        </Box>
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Temps limite avec unité */}
                        <Paper sx={{
                            p: 2,
                            background: 'rgba(51, 65, 85, 0.4)',
                            border: '1px solid rgba(148, 163, 184, 0.2)'
                        }}>
                            <Typography variant="subtitle2" sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, color: '#f1f5f9' }}>
                                <AccessTimeIcon sx={{ fontSize: 18 }} />
                                Temps limite
                            </Typography>
                            <Grid container spacing={2}>
                                <Grid size={{ xs: 7 }}>
                                    <TextField
                                        label="Durée"
                                        name="timeLimit"
                                        type="number"
                                        fullWidth
                                        value={form.timeLimit}
                                        onChange={handleChange}
                                        inputProps={{ min: 1 }}
                                        sx={{
                                            '& .MuiInputBase-input': { color: '#e2e8f0' },
                                            '& .MuiInputLabel-root': { color: '#94a3b8' },
                                            '& .MuiOutlinedInput-root': {
                                                '& fieldset': { borderColor: 'rgba(148, 163, 184, 0.3)' },
                                                '&:hover fieldset': { borderColor: '#94a3b8' },
                                                '&.Mui-focused fieldset': { borderColor: '#667eea' }
                                            }
                                        }}
                                    />
                                </Grid>
                                <Grid size={{ xs: 5 }}>
                                    <FormControl fullWidth>
                                        <InputLabel sx={{ color: '#94a3b8' }}>Unité</InputLabel>
                                        <Select
                                            value={form.timeUnit}
                                            onChange={(e) => setForm({ ...form, timeUnit: e.target.value as TimeUnit })}
                                            label="Unité"
                                            sx={{
                                                color: '#e2e8f0',
                                                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(148, 163, 184, 0.3)' },
                                                '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: '#94a3b8' },
                                                '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#667eea' }
                                            }}
                                        >
                                            <MenuItem value="minutes">Minutes</MenuItem>
                                            <MenuItem value="hours">Heures</MenuItem>
                                            <MenuItem value="days">Jours</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                            </Grid>
                            <Typography variant="caption" sx={{ color: '#94a3b8', mt: 1, display: 'block' }}>
                                Temps alloué : {formatTimeLimit(convertToMinutes(form.timeLimit, form.timeUnit))}
                            </Typography>
                        </Paper>

                        {/* Pourcentage d'ouverture */}
                        <TextField
                            label="Pourcentage d'ouverture du bloc"
                            name="openPercentage"
                            type="number"
                            fullWidth
                            value={form.openPercentage}
                            onChange={handleChange}
                            inputProps={{ min: 0, max: 100 }}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <PercentIcon sx={{ color: '#94a3b8' }} />
                                    </InputAdornment>
                                ),
                            }}
                            helperText="Pourcentage de complétion du bloc requis pour débloquer l'évaluation"
                            sx={{
                                '& .MuiInputBase-input': { color: '#e2e8f0' },
                                '& .MuiInputLabel-root': { color: '#94a3b8' },
                                '& .MuiFormHelperText-root': { color: '#94a3b8' },
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': { borderColor: 'rgba(148, 163, 184, 0.3)' },
                                    '&:hover fieldset': { borderColor: '#94a3b8' },
                                    '&.Mui-focused fieldset': { borderColor: '#667eea' }
                                }
                            }}
                        />

                        <Divider sx={{ borderColor: 'rgba(148, 163, 184, 0.2)' }} />

                        {/* Énoncé texte */}
                        <TextField
                            label="Énoncé (texte)"
                            name="statementText"
                            fullWidth
                            multiline
                            rows={4}
                            value={form.statementText}
                            onChange={handleChange}
                            placeholder="Décrivez les consignes de l'évaluation..."
                            sx={{
                                '& .MuiInputBase-input': { color: '#e2e8f0' },
                                '& .MuiInputLabel-root': { color: '#94a3b8' },
                                '& .MuiOutlinedInput-root': {
                                    '& fieldset': { borderColor: 'rgba(148, 163, 184, 0.3)' },
                                    '&:hover fieldset': { borderColor: '#94a3b8' },
                                    '&.Mui-focused fieldset': { borderColor: '#667eea' }
                                }
                            }}
                        />

                        {/* Upload de fichiers */}
                        <Box>
                            <Button
                                variant="outlined"
                                component="label"
                                startIcon={<AttachFileIcon />}
                                fullWidth
                                sx={{
                                    borderStyle: 'dashed',
                                    borderWidth: 2,
                                    borderColor: '#94a3b8',
                                    color: '#e2e8f0',
                                    py: 1.5,
                                    '&:hover': {
                                        borderStyle: 'dashed',
                                        borderColor: '#667eea',
                                        background: 'rgba(103, 126, 234, 0.1)'
                                    }
                                }}
                            >
                                Ajouter des fichiers (PDF, images, etc.)
                                <input
                                    type="file"
                                    hidden
                                    multiple
                                    onChange={handleFileChange}
                                />
                            </Button>
                        </Box>

                        {/* Liste des fichiers */}
                        {files.length > 0 && (
                            <Paper sx={{
                                background: 'rgba(51, 65, 85, 0.4)',
                                border: '1px solid rgba(148, 163, 184, 0.2)'
                            }}>
                                <List>
                                    {files.map((file, index) => (
                                        <React.Fragment key={index}>
                                            <ListItem>
                                                <AttachFileIcon sx={{ mr: 2, color: '#94a3b8' }} />
                                                <ListItemText
                                                    primary={file.name}
                                                    secondary={`${(file.size / 1024).toFixed(2)} KB`}
                                                    primaryTypographyProps={{ variant: 'body2', color: '#e2e8f0' }}
                                                    secondaryTypographyProps={{ variant: 'caption', color: '#94a3b8' }}
                                                />
                                                <ListItemSecondaryAction>
                                                    <IconButton
                                                        edge="end"
                                                        onClick={() => handleRemoveFile(index)}
                                                        sx={{
                                                            color: '#ef4444',
                                                            '&:hover': { background: 'rgba(239, 68, 68, 0.1)' }
                                                        }}
                                                    >
                                                        <DeleteIcon />
                                                    </IconButton>
                                                </ListItemSecondaryAction>
                                            </ListItem>
                                            {index < files.length - 1 && <Divider sx={{ borderColor: 'rgba(148, 163, 184, 0.1)' }} />}
                                        </React.Fragment>
                                    ))}
                                </List>
                            </Paper>
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions sx={{
                    borderTop: '1px solid rgba(148, 163, 184, 0.2)',
                    p: 3,
                    gap: 2
                }}>
                    <Button
                        onClick={handleClose}
                        sx={{
                            color: '#94a3b8',
                            '&:hover': { background: 'rgba(148, 163, 184, 0.1)' }
                        }}
                    >
                        Annuler
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={!form.title || !form.blockId}
                        sx={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            px: 4,
                            '&:hover': {
                                background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                            },
                            '&:disabled': {
                                background: 'rgba(148, 163, 184, 0.2)',
                                color: '#64748b'
                            }
                        }}
                    >
                        Créer l'évaluation
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={openTrackingDialog}
                onClose={handleCloseTracking}
                maxWidth="lg"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 4, overflow: 'hidden' }
                }}
            >
                {selectedTracking && (
                    <>
                        <Box sx={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            color: 'white',
                            p: 3,
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center'
                        }}>
                            <Box>
                                <Typography variant="h5" sx={{ fontWeight: 600, fontFamily: 'Quicksand, sans-serif' }}>
                                    Détails de l'évaluation : {selectedTracking.evaluation.title}
                                </Typography>
                                <Typography variant="subtitle1" sx={{ opacity: 0.9, mt: 0.5 }}>
                                    Apprenant : {selectedTracking.learner.firstName} {selectedTracking.learner.lastName}
                                </Typography>
                            </Box>
                            <IconButton onClick={handleCloseTracking} sx={{ color: 'white', '&:hover': { background: 'rgba(255,255,255,0.2)' } }}>
                                <CloseIcon />
                            </IconButton>
                        </Box>

                        <DialogContent sx={{ p: 4, background: '#f8fafc' }}>
                            <Card sx={{ mb: 4, p: 3, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: '1px solid rgba(0,0,0,0.05)' }}>
                                <Stepper activeStep={getStepperActiveStep(selectedTracking.status)} alternativeLabel>
                                    <Step><StepLabel>Fermée</StepLabel></Step>
                                    <Step><StepLabel>Ouverte</StepLabel></Step>
                                    <Step><StepLabel error={selectedTracking.status === 'Dépassé'}>En cours / Dépassé</StepLabel></Step>
                                    <Step><StepLabel>Rendu</StepLabel></Step>
                                    <Step><StepLabel error={selectedTracking.status === 'Corrigé et non validée'}>Corrigée</StepLabel></Step>
                                </Stepper>
                            </Card>

                            <Grid container spacing={4}>
                                <Grid size={{ xs: 12, md: 5 }}>
                                    <Card sx={{ height: '100%', borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: '1px solid rgba(0,0,0,0.05)' }}>
                                        <CardContent>
                                            <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600, mb: 2, color: '#1e293b', display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <InfoIcon sx={{ color: '#667eea' }} /> Métadonnées
                                            </Typography>
                                            <List dense>
                                                <ListItem sx={{ px: 0 }}>
                                                    <ListItemText primary="Date d'ouverture" secondary={selectedTracking.submission?.startedAt ? format(new Date(selectedTracking.submission.startedAt), 'dd MMM yyyy à HH:mm', { locale: fr }) : 'Non démarrée'} />
                                                </ListItem>
                                                <Divider />
                                                <ListItem sx={{ px: 0, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                                    <ListItemText primary="Date limite" secondary={selectedTracking.submission?.deadline ? format(new Date(selectedTracking.submission.deadline), 'dd MMM yyyy à HH:mm', { locale: fr }) : '-'} />
                                                    {selectedTracking.submission && (
                                                        <Button
                                                            size="small"
                                                            variant="outlined"
                                                            startIcon={<CalendarMonthIcon fontSize="small" />}
                                                            onClick={handleOpenExtendDialog}
                                                            sx={{
                                                                color: '#667eea', borderColor: '#667eea', borderRadius: 2, textTransform: 'none',
                                                                '&:hover': { background: 'rgba(102, 126, 234, 0.1)', borderColor: '#764ba2' }
                                                            }}
                                                        >
                                                            Reporter
                                                        </Button>
                                                    )}
                                                </ListItem>
                                                <Divider />
                                                <ListItem sx={{ px: 0, flexDirection: 'column', alignItems: 'flex-start' }}>
                                                    <ListItemText primary="Date de rendu" secondary={selectedTracking.submission?.submittedAt ? format(new Date(selectedTracking.submission.submittedAt), 'dd MMM yyyy à HH:mm', { locale: fr }) : '-'} />

                                                    {selectedTracking.submission?.deadlineExtensions && selectedTracking.submission.deadlineExtensions.length > 0 && (
                                                        <Box sx={{ mt: 2, p: 2, width: '100%', bgcolor: 'rgba(102, 126, 234, 0.05)', borderLeft: '3px solid #667eea', borderRadius: '0 8px 8px 0' }}>
                                                            <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#667eea', mb: 1 }}>
                                                                <HistoryIcon fontSize="small" /> Historique des reports
                                                            </Typography>
                                                            <List dense disablePadding>
                                                                {selectedTracking.submission.deadlineExtensions.map((ext, idx) => (
                                                                    <ListItem key={idx} disableGutters sx={{ alignItems: 'flex-start', pb: 2 }}>
                                                                        <Box sx={{ width: '100%' }}>
                                                                            <Typography variant="caption" sx={{ fontWeight: 700, color: '#1e293b', display: 'block', mb: 0.5 }}>
                                                                                Reporté au {format(new Date(ext.newDeadline), 'dd MMMM yyyy', { locale: fr })} {format(new Date(ext.newDeadline), 'HH:mm', { locale: fr }).replace(':', 'h')}
                                                                            </Typography>
                                                                            <Typography variant="caption" sx={{ color: '#64748b', display: 'block', fontSize: '0.75rem' }}>
                                                                                Initialement prévu le {format(new Date(ext.oldDeadline), 'dd MMMM yyyy', { locale: fr })} {format(new Date(ext.oldDeadline), 'HH:mm', { locale: fr }).replace(':', 'h')}
                                                                            </Typography>
                                                                            {ext.reason && (
                                                                                <Typography variant="caption" sx={{ color: '#475569', fontStyle: 'italic', display: 'block', mt: 0.5, pl: 1, borderLeft: '2px solid rgba(0,0,0,0.1)' }}>
                                                                                    "{ext.reason}"
                                                                                </Typography>
                                                                            )}
                                                                        </Box>
                                                                    </ListItem>
                                                                ))}
                                                            </List>
                                                        </Box>
                                                    )}
                                                </ListItem>
                                            </List>
                                        </CardContent>
                                    </Card>
                                </Grid>

                                <Grid size={{ xs: 12, md: 7 }}>
                                    <Stack spacing={4} sx={{ height: '100%' }}>
                                        <Card sx={{ flex: 1, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: '1px solid rgba(0,0,0,0.05)' }}>
                                            <CardContent>
                                                <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600, mb: 2, color: '#1e293b' }}>Rendu de l'apprenant</Typography>
                                                {selectedTracking.submission?.submittedAt ? (
                                                    <Box sx={{ p: 2, bgcolor: '#f1f5f9', borderRadius: 2 }}>
                                                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 2, color: '#475569' }}>
                                                            {selectedTracking.submission.answers || 'Aucun texte fourni.'}
                                                        </Typography>
                                                        {selectedTracking.submission.files && selectedTracking.submission.files.length > 0 && (
                                                            <Box sx={{ mt: 3 }}>
                                                                <Typography variant="caption" sx={{ fontWeight: 600, color: '#64748b', textTransform: 'uppercase', letterSpacing: 0.5 }}>Pièces jointes</Typography>
                                                                <List dense sx={{ mt: 1 }}>
                                                                    {selectedTracking.submission.files.map((file, idx) => (
                                                                        <ListItem key={idx} sx={{ px: 2, bgcolor: 'white', borderRadius: 2, mb: 1, border: '1px solid #e2e8f0' }}>
                                                                            <AttachFileIcon fontSize="small" sx={{ mr: 1, color: '#64748b' }} />
                                                                            <a href={file.url} target="_blank" rel="noopener noreferrer" style={{ fontSize: '0.875rem', color: '#3b82f6', textDecoration: 'none', fontWeight: 500 }}>
                                                                                {file.name}
                                                                            </a>
                                                                        </ListItem>
                                                                    ))}
                                                                </List>
                                                            </Box>
                                                        )}
                                                    </Box>
                                                ) : (
                                                    <Box sx={{ p: 4, textAlign: 'center', bgcolor: '#f8fafc', borderRadius: 2, border: '1px dashed #cbd5e1' }}>
                                                        <AssignmentIcon sx={{ fontSize: 48, color: '#cbd5e1', mb: 1 }} />
                                                        <Typography variant="body2" sx={{ color: '#64748b' }}>L'apprenant n'a pas encore rendu son évaluation.</Typography>
                                                    </Box>
                                                )}
                                            </CardContent>
                                        </Card>

                                        <Card sx={{ flex: 1, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: '1px solid rgba(0,0,0,0.05)' }}>
                                            <CardContent>
                                                <Typography variant="h6" sx={{ fontSize: '1.1rem', fontWeight: 600, mb: 2, color: '#1e293b' }}>Correction</Typography>
                                                <Box sx={{ p: 2, borderRadius: 2, background: selectedTracking.submission?.gradedAt ? 'rgba(74, 222, 128, 0.1)' : '#f1f5f9', border: selectedTracking.submission?.gradedAt ? '1px solid rgba(74, 222, 128, 0.3)' : 'none' }}>
                                                    {selectedTracking.submission?.gradedAt ? (
                                                        <>
                                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                                                                <CheckCircleIcon sx={{ color: '#22c55e' }} />
                                                                <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#166534' }}>Note : {selectedTracking.submission.grade} / 20</Typography>
                                                            </Box>
                                                            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: '#166534', mb: 2 }}>
                                                                {selectedTracking.submission.feedback || 'Aucun commentaire.'}
                                                            </Typography>
                                                            <Divider sx={{ my: 1, borderColor: 'rgba(22, 101, 52, 0.2)' }} />
                                                            <Typography variant="caption" sx={{ color: '#166534', fontStyle: 'italic', display: 'block' }}>
                                                                Certifié numériquement par le formateur le {format(new Date(selectedTracking.submission.gradedAt), 'dd/MM/yyyy à HH:mm')}
                                                                {selectedTracking.submission.serverSignature && " • Garantie d'intégrité serveur OK"}
                                                            </Typography>
                                                        </>
                                                    ) : isTrainerOrAdmin && selectedTracking.submission?.submittedAt ? (
                                                        <Stack spacing={2}>
                                                            <TextField
                                                                label="Note / 20"
                                                                type="number"
                                                                fullWidth
                                                                size="small"
                                                                inputProps={{ min: 0, max: 20, step: 0.5 }}
                                                                value={gradingForm.grade}
                                                                onChange={(e) => setGradingForm({...gradingForm, grade: e.target.value})}
                                                                sx={{ bgcolor: 'white' }}
                                                            />
                                                            <TextField
                                                                label="Commentaires et feedback"
                                                                multiline
                                                                rows={3}
                                                                fullWidth
                                                                size="small"
                                                                value={gradingForm.feedback}
                                                                onChange={(e) => setGradingForm({...gradingForm, feedback: e.target.value})}
                                                                sx={{ bgcolor: 'white' }}
                                                            />
                                                            <Button
                                                                variant="contained"
                                                                fullWidth
                                                                onClick={handleGradeSubmission}
                                                                disabled={isGrading || gradingForm.grade === ''}
                                                                startIcon={isGrading ? <CircularProgress size={20} color="inherit" /> : <CheckCircleIcon />}
                                                                sx={{
                                                                    mt: 1,
                                                                    bgcolor: '#22c55e',
                                                                    '&:hover': { bgcolor: '#16a34a' },
                                                                    textTransform: 'none',
                                                                    fontWeight: 600
                                                                }}
                                                            >
                                                                {isGrading ? 'Signature en cours...' : 'Valider et Signer numériquement'}
                                                            </Button>
                                                        </Stack>
                                                    ) : (
                                                        <Typography variant="body2" sx={{ color: '#64748b', fontStyle: 'italic', textAlign: 'center', py: 2 }}>En attente de correction...</Typography>
                                                    )}
                                                </Box>
                                            </CardContent>
                                        </Card>
                                    </Stack>
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <DialogActions sx={{ p: 3, background: 'white', borderTop: '1px solid rgba(0,0,0,0.05)' }}>
                            <Button onClick={handleCloseTracking} variant="outlined" sx={{ borderRadius: 2, textTransform: 'none', px: 3, color: '#64748b', borderColor: '#cbd5e1' }}>Fermer</Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>

            {/* Extend Deadline Modal */}
            <Dialog
                open={openExtendDialog}
                onClose={handleCloseExtendDialog}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                    sx: { borderRadius: 4, overflow: 'hidden' }
                }}
            >
                <Box sx={{
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: 'white',
                    p: 3,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 2
                }}>
                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 48, height: 48, backdropFilter: 'blur(10px)' }}>
                        <CalendarMonthIcon />
                    </Avatar>
                    <Box>
                        <Typography variant="h6" sx={{ fontWeight: 600, fontFamily: 'Quicksand, sans-serif' }}>
                            Reporter la date limite
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                            Accorder un délai supplémentaire à l'apprenant
                        </Typography>
                    </Box>
                    <IconButton onClick={handleCloseExtendDialog} sx={{ ml: 'auto', color: 'white', '&:hover': { background: 'rgba(255,255,255,0.2)' } }}>
                        <CloseIcon />
                    </IconButton>
                </Box>

                <DialogContent sx={{ p: 4, background: '#f8fafc' }}>
                    <Card sx={{ mb: 4, p: 2, borderRadius: 3, boxShadow: '0 4px 20px rgba(0,0,0,0.05)', border: '1px solid rgba(0,0,0,0.05)', display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: '#e2e8f0', color: '#475569' }}>
                            <InfoIcon />
                        </Avatar>
                        <Box>
                            <Typography variant="body2" sx={{ color: '#475569', fontWeight: 600 }}>
                                {selectedTracking?.learner.firstName} {selectedTracking?.learner.lastName}
                            </Typography>
                            {selectedTracking?.submission?.deadline ? (
                                <Typography variant="caption" sx={{ color: '#64748b' }}>
                                    Ancienne date : {format(new Date(selectedTracking.submission.deadline), 'dd MMM yyyy à HH:mm', { locale: fr })}
                                </Typography>
                            ) : (
                                <Typography variant="caption" sx={{ color: '#64748b' }}>
                                    Aucune date limite définie.
                                </Typography>
                            )}
                        </Box>
                    </Card>

                    <Stack spacing={3}>
                        <Grid container spacing={3}>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    label="Nouvelle Date"
                                    type="date"
                                    fullWidth
                                    variant="outlined"
                                    InputLabelProps={{ shrink: true }}
                                    value={extendForm.date}
                                    onChange={(e) => setExtendForm({ ...extendForm, date: e.target.value })}
                                />
                            </Grid>
                            <Grid size={{ xs: 12, sm: 6 }}>
                                <TextField
                                    label="Heure (optionnelle)"
                                    type="time"
                                    fullWidth
                                    variant="outlined"
                                    InputLabelProps={{ shrink: true }}
                                    value={extendForm.time}
                                    onChange={(e) => setExtendForm({ ...extendForm, time: e.target.value })}
                                    InputProps={{
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <AccessTimeIcon fontSize="small" />
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </Grid>
                        </Grid>
                        <TextField
                            label="Raison du report"
                            multiline
                            rows={3}
                            fullWidth
                            variant="outlined"
                            placeholder="Entrez le motif du report (ex: Problème technique certifié, absence justifiée...)"
                            value={extendForm.reason}
                            onChange={(e) => setExtendForm({ ...extendForm, reason: e.target.value })}
                        />
                    </Stack>
                </DialogContent>
                <DialogActions sx={{ p: 3, background: 'white', borderTop: '1px solid rgba(0,0,0,0.05)' }}>
                    <Button onClick={handleCloseExtendDialog} variant="outlined" sx={{ borderRadius: 2, textTransform: 'none', px: 3, color: '#64748b', borderColor: '#cbd5e1' }}>
                        Annuler
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleExtendDeadline}
                        disabled={!extendForm.date || !extendForm.reason}
                        sx={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', px: 3, borderRadius: 2, textTransform: 'none', boxShadow: '0 4px 14px rgba(102, 126, 234, 0.4)' }}
                    >
                        Valider le report
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Modal de Détails du Modèle d'Évaluation */}
            <Dialog
                open={openModelDetailDialog}
                onClose={handleCloseModelDetail}
                maxWidth="md"
                fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: 4,
                        overflow: 'hidden',
                        background: '#f8fafc'
                    }
                }}
            >
                {selectedEvaluation && (
                    <>
                        <Box sx={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            px: 3, py: 4, color: 'white', position: 'relative'
                        }}>
                            <IconButton
                                onClick={handleCloseModelDetail}
                                sx={{ position: 'absolute', right: 8, top: 8, color: 'white' }}
                            >
                                <CloseIcon />
                            </IconButton>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 1 }}>
                                <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', width: 64, height: 64 }}>
                                    <AssignmentIcon sx={{ fontSize: 32 }} />
                                </Avatar>
                                <Box>
                                    <Typography variant="h5" sx={{ fontWeight: 700 }}>{selectedEvaluation.title}</Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.9 }}>Détails du modèle d'évaluation</Typography>
                                </Box>
                            </Box>
                        </Box>

                        <DialogContent sx={{ p: 4 }}>
                            <Grid container spacing={4}>
                                {/* Gauche : Métadonnées */}
                                <Grid size={{ xs: 12, md: 5 }}>
                                    <Typography variant="overline" sx={{ color: '#64748b', fontWeight: 700, letterSpacing: 1.2 }}>
                                        Configuration
                                    </Typography>
                                    <Paper elevation={0} sx={{ p: 3, borderRadius: 3, bgcolor: 'white', mt: 1, border: '1px solid #e2e8f0' }}>
                                        <Stack spacing={2}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Avatar sx={{ bgcolor: '#eff6ff', color: '#3b82f6', width: 40, height: 40 }}>
                                                    <FolderIcon />
                                                </Avatar>
                                                <Box>
                                                    <Typography variant="caption" sx={{ color: '#64748b' }}>Bloc de compétences</Typography>
                                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{selectedEvaluation.blockId?.title || 'Non assigné'}</Typography>
                                                </Box>
                                            </Box>
                                            <Divider />
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Avatar sx={{ bgcolor: '#f0fdf4', color: '#22c55e', width: 40, height: 40 }}>
                                                    <AccessTimeIcon />
                                                </Avatar>
                                                <Box>
                                                    <Typography variant="caption" sx={{ color: '#64748b' }}>Temps autorisé</Typography>
                                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{formatTimeLimit(selectedEvaluation.timeLimitMinutes)}</Typography>
                                                </Box>
                                            </Box>
                                            <Divider />
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                <Avatar sx={{ bgcolor: '#fff7ed', color: '#f97316', width: 40, height: 40 }}>
                                                    <PercentIcon />
                                                </Avatar>
                                                <Box>
                                                    <Typography variant="caption" sx={{ color: '#64748b' }}>Déblocage</Typography>
                                                    <Typography variant="body1" sx={{ fontWeight: 600 }}>{selectedEvaluation.openPercentage}% du bloc complété</Typography>
                                                </Box>
                                            </Box>
                                        </Stack>
                                    </Paper>
                                </Grid>

                                {/* Droite : Énoncé */}
                                <Grid size={{ xs: 12, md: 7 }}>
                                    <Typography variant="overline" sx={{ color: '#64748b', fontWeight: 700, letterSpacing: 1.2 }}>
                                        Contenu de l'énoncé
                                    </Typography>
                                    <Paper elevation={0} sx={{ p: 3, borderRadius: 3, bgcolor: 'white', mt: 1, border: '1px solid #e2e8f0', minHeight: 200 }}>
                                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', color: '#1e293b', mb: 3 }}>
                                            {selectedEvaluation.statementText || "Aucun texte d'énoncé fourni."}
                                        </Typography>

                                        {selectedEvaluation.statementFiles && selectedEvaluation.statementFiles.length > 0 && (
                                            <Box>
                                                <Typography variant="subtitle2" sx={{ mb: 1.5, display: 'flex', alignItems: 'center', gap: 1 }}>
                                                    <AttachFileIcon sx={{ fontSize: 18, color: '#64748b' }} />
                                                    Documents joints ({selectedEvaluation.statementFiles.length})
                                                </Typography>
                                                <Stack spacing={1}>
                                                    {selectedEvaluation.statementFiles.map((fileUrl, idx) => (
                                                        <Button
                                                            key={idx}
                                                            variant="outlined"
                                                            startIcon={<DescriptionIcon />}
                                                            href={fileUrl}
                                                            target="_blank"
                                                            sx={{
                                                                justifyContent: 'flex-start',
                                                                textTransform: 'none',
                                                                borderRadius: 2,
                                                                borderColor: '#e2e8f0',
                                                                color: '#475569',
                                                                '&:hover': { bgcolor: '#f1f5f9', borderColor: '#cbd5e1' }
                                                            }}
                                                        >
                                                            Fichier énoncé #{idx + 1}
                                                        </Button>
                                                    ))}
                                                </Stack>
                                            </Box>
                                        )}
                                    </Paper>
                                </Grid>
                            </Grid>
                        </DialogContent>
                        <DialogActions sx={{ p: 3, bgcolor: '#f1f5f9' }}>
                            <Button
                                onClick={handleCloseModelDetail}
                                variant="contained"
                                sx={{
                                    bgcolor: '#667eea',
                                    '&:hover': { bgcolor: '#5a6fd6' },
                                    borderRadius: 2,
                                    px: 4,
                                    textTransform: 'none',
                                    fontWeight: 600
                                }}
                            >
                                Fermer
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
        </Box>
    );
};

export default BlockEvaluationPage;
